import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { FirestoreDocumentSnapshot, ITripSearch, TripSearchResult } from "@cruisen/contract"
import { CruiseSearchResult, CruiseSearchResultsQuery } from "@cruisen/react-shared"
import debounce from "lodash.debounce"
import { doc, collection, getDoc, getDocs, onSnapshot } from "firebase/firestore"
import { firestore } from "../firebase"

export const transformDocSnapshotsToSearchResults = (
  docSnapshots: FirestoreDocumentSnapshot[],
): { searchResults: CruiseSearchResult[]; searchCriteriaHash: string } => {
  const searchResults: CruiseSearchResult[] = []
  let searchCriteriaHash = ""

  for (const docSnapshot of docSnapshots) {
    const docData = docSnapshot.data() as Omit<TripSearchResult, "id">

    if (docData.searchResultStatus !== "irrelevant") {
      if (searchCriteriaHash === "") {
        searchCriteriaHash = docData.searchCriteriaHash
      }

      const id = docSnapshot.id.toString()

      searchResults.push({
        id,
        docId: id,
        tripSearchId: docData.tripSearchId,
        name: docData.name,
        status: docData.searchResultStatus,
        startDate: new Date(docData.startDate),
        endDate: new Date(docData.endDate),
        shipName: docData.shipName,
        ports: docData.ports,
        lineLogoImage: docData.lineLogo,
        image: docData.heroImage,
        price: docData.lowestPrice,
        pricePP: docData.lowestPricePP,
        cabinsScanStatus: docData.cabinsScanStatus,
        shipLaunchDate: new Date(docData.shipLaunchDate),
        lineId: docData.lineId,
        lineName: docData.lineName,
        likes: docData.likes,
        tags: docData.tags,
        cabinGradeTypes: docData.cabinGradeTypes,
      })
    }
  }

  return { searchCriteriaHash, searchResults }
}

export const subscribeCruiseSearchResults = (
  tripId: string,
  onResultsUpdate: (results: CruiseSearchResultsQuery) => void,
): (() => void) => {
  const tripSearchId = "main"

  let state: CruiseSearchResultsQuery = {
    status: "isLoading",
    cruises: [],
    tripSearchId,
    searchCriteriaHash: "",
  }

  const updateResults = (patch: Partial<CruiseSearchResultsQuery>) => {
    const newState = {
      ...state,
      ...patch,
    }

    // Skip update if it is still in loading state
    if (newState.status === "isLoading" && newState.status === state.status) {
      return
    }

    state = newState
    onResultsUpdate(state)
  }

  let resultsSubscription: (() => void) | undefined
  const unsubscribeResults = () => {
    if (resultsSubscription) {
      resultsSubscription()
      resultsSubscription = undefined
    }
  }

  try {
    const searchDocRef = doc(
      collection(doc(collection(firestore, "Trips"), tripId), "Searches"),
      tripSearchId,
    )

    let searchDoc: Omit<ITripSearch, "id"> | undefined
    let searchResults: CruiseSearchResult[] = []

    const subscribeResults = () => {
      const resultsRef = collection(searchDocRef, "Results")
      resultsSubscription = onSnapshot(resultsRef, async (resultSnapshots) => {
        if (resultSnapshots.empty) {
          updateResults({ status: "notFound", cruises: [], tripSearchId })
          return
        }

        const { searchResults: results, searchCriteriaHash } = transformDocSnapshotsToSearchResults(
          resultSnapshots.docs,
        )
        searchResults = results

        if (searchResults.length === 0) {
          updateResults({ status: "notFound", cruises: [], tripSearchId })
          return
        }

        if (
          searchDoc?.searchResultsCount &&
          searchDoc.searchResultsCount !== searchResults.length
        ) {
          updateResults({ status: "isLoading", cruises: [], tripSearchId })
          return
        }

        updateResults({
          status: "success",
          cruises: searchResults,
          tripSearchId,
          searchCriteriaHash,
        })
      })
    }

    const unsubscribeSearch = onSnapshot(searchDocRef, async (searchSnapshot) => {
      const tripSearchId = searchSnapshot.id

      if (!searchSnapshot.exists()) {
        updateResults({ status: "isLoading", cruises: [], tripSearchId })
        return
      }

      searchDoc = searchSnapshot.data() as Omit<ITripSearch, "id">

      if (searchDoc.status === "waiting") {
        unsubscribeResults()
        updateResults({ status: "isLoading", cruises: [], tripSearchId })
        return
      }

      const resultsRef = collection(searchDocRef, "Results")
      const resultSnapshots = await getDocs(resultsRef)

      if (searchDoc.searchResultsCount && searchDoc.searchResultsCount === resultSnapshots.size) {
        if (resultSnapshots.empty) {
          updateResults({
            status: "notFound",
            cruises: [],
            tripSearchId,
            searchCriteriaHash: "",
          })
        }

        const { searchResults, searchCriteriaHash } = transformDocSnapshotsToSearchResults(
          resultSnapshots.docs,
        )

        if (searchResults.length === 0) {
          updateResults({
            status: "notFound",
            cruises: [],
            tripSearchId,
            searchCriteriaHash: "",
          })
        }

        updateResults({
          status: "success",
          cruises: searchResults,
          tripSearchId,
          searchCriteriaHash,
        })
      }

      if (!resultsSubscription) {
        subscribeResults()
      }
    })

    return () => {
      unsubscribeSearch()
      unsubscribeResults()
    }
  } catch (error) {
    console.error("cruise search results subscription failure", error)
    updateResults({ status: "error", cruises: [], tripSearchId: "" })
    return () => undefined
  }
}

export const getCruiseSearchResults = async (tripId: string): Promise<CruiseSearchResultsQuery> => {
  try {
    const mainSearchDocRef = doc(
      collection(doc(collection(firestore, "Trips"), tripId), "Searches"),
      "main",
    )

    const searchSnapshot = await getDoc(mainSearchDocRef)
    const tripSearchId = searchSnapshot.id

    if (!searchSnapshot.exists()) {
      return {
        status: "isLoading",
        cruises: [],
        tripSearchId: "",
        searchCriteriaHash: "",
      }
    }

    const searchDoc = searchSnapshot.data() as Omit<ITripSearch, "id">

    if (searchDoc.status === "waiting") {
      return {
        status: "isLoading",
        cruises: [],
        tripSearchId,
        searchCriteriaHash: "",
      }
    }

    const resultsRef = collection(mainSearchDocRef, "Results")
    const resultSnapshots = await getDocs(resultsRef)

    if (resultSnapshots.empty) {
      return {
        status: "notFound",
        cruises: [],
        tripSearchId,
        searchCriteriaHash: "",
      }
    }

    const { searchResults, searchCriteriaHash } = transformDocSnapshotsToSearchResults(
      resultSnapshots.docs,
    )

    if (searchResults.length === 0) {
      return {
        status: "notFound",
        cruises: [],
        tripSearchId,
        searchCriteriaHash: "",
      }
    }

    return {
      status: "success",
      cruises: searchResults,
      tripSearchId,
      searchCriteriaHash,
    }
  } catch (error) {
    console.log(error)
    return {
      status: "error",
      cruises: [],
      tripSearchId: "",
      searchCriteriaHash: "",
    }
  }
}

let unsubscribeResults: (() => void) | undefined

export const useCruiseSearchResults = (tripId: string) => {
  const queryClient = useQueryClient()
  const [isDebounceLoading, setIsDebounceLoading] = useState(false)

  const queryData = useQuery<CruiseSearchResultsQuery>({
    queryKey: ["cruises", tripId],
    queryFn: () => getCruiseSearchResults(tripId),
    initialData: {
      status: "isLoading",
      tripSearchId: "",
      cruises: [],
      searchCriteriaHash: "",
    },
    enabled: !!tripId,
  })

  useEffect(() => {
    if (!tripId) return
    if (unsubscribeResults) unsubscribeResults()

    const debouncedSave = debounce((results) => {
      setIsDebounceLoading(false)
      queryClient.setQueryData(["cruises", tripId], results)
    }, 1000)

    const unsubscribe = subscribeCruiseSearchResults(
      tripId,
      (results: CruiseSearchResultsQuery) => {
        setIsDebounceLoading(true)
        debouncedSave(results)
      },
    )

    unsubscribeResults = unsubscribe

    return () => {
      unsubscribe()
      debouncedSave.cancel()
    }
  }, [tripId])

  return { ...queryData, isDebounceLoading }
}
